import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Maximize2, MessageSquare, Plus, Scan, Send, X, ZoomIn, ZoomOut } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "./ui/dialog";
import { chatAPI } from "../lib/api";
import { useOptionalActiveLocation } from "../context/ActiveLocationContext";
import { resolveDesignTheme } from "../lib/designThemes";
import HelpChatSupportDialog from './HelpChatSupportDialog';

const HELP_CHAT_ERROR_TEXT =
  "Leider gibt es gerade einen Fehler. Bitte versuche es später nochmal.";
const GREETING = { role: "bot", text: "Hallo, ich bin Mia, deine Assistenz rund um die Anwendung. Wie kann ich dir helfen?" };

function newConversationId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) return null;
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Nur statische Hilfe-Bilder von dieser Anwendung laden, ohne URLs aus dem Chat.
const isSafeImageSrc = (src) =>
  typeof src === "string" && /^\/help\/(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]+\.(?:png|jpe?g|webp)$/.test(src);

const helpArea = (pathname) => {
  const routes = [
    ["/clients/data-checks", "data-checks"], ["/marketing/automations/instagram", "instagram"],
    ["/marketing/automations", "automations"], ["/marketing/newsletter", "newsletter"],
    ["/messenger", "messenger"], ["/clients", "patients"],
    ["/content-bibliothek", "content"], ["/anfragen", "inquiries"],
    ...["dashboard", "calendar", "locations", "services", "materials", "staff", "work-schedule", "waitlist", "vouchers", "messenger", "shop", "forms", "reports", "settings", "setup", "support", "api-docs"].map(area => [`/${area}`, area]),
  ];
  return routes.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1];
};

function HelpScreenshot({ src, alt, onLoad }) {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [previewFailed, setPreviewFailed] = useState(false);
  const viewportRef = useRef(null);
  const caption = alt || "Screenshot aus der Anwendung";
  const fitToWindow = () => {
    setZoom(1);
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    }
  };
  if (failed) return <p className="text-xs text-[#86868B]">Screenshot konnte nicht geladen werden. Die Schritte stehen im Text darüber.</p>;
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (nextOpen) { setZoom(1); setPreviewFailed(false); }
    }}>
      <figure className="my-3 overflow-hidden rounded-xl border border-[#E5E5EA] bg-white">
        <DialogTrigger asChild>
          <button type="button" aria-label={`${caption} vergrößern`} className="group relative block w-full overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171014]">
            <img src={src} alt={caption} loading="lazy" onLoad={onLoad} onError={() => setFailed(true)} className="w-full transition-transform duration-200 group-hover:scale-[1.015] motion-reduce:transform-none motion-reduce:transition-none" />
            <span aria-hidden="true" className="absolute bottom-2 right-2 grid h-8 w-8 place-items-center rounded-full bg-[#171014]/85 text-white shadow-sm"><Maximize2 className="h-4 w-4" /></span>
          </button>
        </DialogTrigger>
        <figcaption className="px-3 py-2 text-xs font-normal leading-4 text-[#6E6E73]">{alt || "Ansicht aus dem Beispielkonto"} · Zum Vergrößern anklicken</figcaption>
      </figure>
      <DialogContent hideClose autoLoading={false} className="flex h-[min(86dvh,900px)] max-w-[1200px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:p-0">
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#E5E5EA] px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <DialogTitle className="truncate text-sm font-semibold leading-5 text-[#171014]">{caption}</DialogTitle>
            <DialogDescription className="mt-1 text-xs text-[#6E6E73]">Originalaufnahme · Zum Zoomen auf das Bild klicken</DialogDescription>
          </div>
          <DialogClose asChild><button type="button" aria-label="Bild schließen" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[#171014] hover:bg-[#F0F0F2] focus-visible:outline focus-visible:outline-2"><X className="h-5 w-5" /></button></DialogClose>
        </div>
        <div ref={viewportRef} className="min-h-0 flex-1 overflow-auto bg-[#F0F0F2] overscroll-contain">
          {previewFailed ? <p role="alert" className="p-6 text-sm text-[#6E6E73]">Das Bild konnte nicht geladen werden. Schließe die Vorschau und versuche es erneut.</p> : (
            <div className="transition-[width,height] duration-200 motion-reduce:transition-none" style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
              <button type="button" aria-label={zoom > 1 ? "Bild auf Fenstergröße verkleinern" : "Bild vergrößern"} onClick={() => zoom > 1 ? fitToWindow() : setZoom(2)} className={`block h-full w-full animate-in fade-in zoom-in-95 duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#171014] motion-reduce:animate-none ${zoom > 1 ? "cursor-zoom-out" : "cursor-zoom-in"}`}>
                <img src={src} alt={caption} draggable={false} onError={() => setPreviewFailed(true)} className="h-full w-full max-w-none object-contain" />
              </button>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[#E5E5EA] px-3 py-2 sm:px-5">
          <span className="hidden text-xs text-[#6E6E73] sm:block">Vergrößertes Bild durch Scrollen erkunden</span>
          <div className="ml-auto flex items-center gap-1">
            <button type="button" aria-label="Verkleinern" disabled={zoom <= 1 || previewFailed} onClick={() => setZoom(value => Math.max(1, value - 0.5))} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-[#F0F0F2] disabled:opacity-35 focus-visible:outline focus-visible:outline-2"><ZoomOut className="h-4 w-4" /></button>
            <output aria-label="Zoomstufe" className="min-w-[3.5rem] text-center text-xs font-semibold tabular-nums text-[#171014]">{Math.round(zoom * 100)} %</output>
            <button type="button" aria-label="Vergrößern" disabled={zoom >= 3 || previewFailed} onClick={() => setZoom(value => Math.min(3, value + 0.5))} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-[#F0F0F2] disabled:opacity-35 focus-visible:outline focus-visible:outline-2"><ZoomIn className="h-4 w-4" /></button>
            <button type="button" aria-label="An Fenster anpassen" onClick={fitToWindow} disabled={previewFailed} className="ml-2 grid h-10 w-10 place-items-center rounded-lg border border-[#E5E5EA] hover:bg-[#F0F0F2] disabled:opacity-35 focus-visible:outline focus-visible:outline-2"><Scan className="h-4 w-4" /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const isSafeLinkHref = (href) =>
  typeof href === "string" &&
  ((href.startsWith("/") && !href.startsWith("//")) ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:"));

// Minimaler, sicherer Markdown-Renderer für Bot-Antworten:
// unterstützt ![alt](url), [text](url) und **fett**. Reicht für Mias Antworten.
function renderInline(text, keyPrefix) {
  const nodes = [];
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] != null) {
      nodes.push(
        <strong key={`${keyPrefix}-b-${i}`} className="font-semibold">
          {match[1]}
        </strong>,
      );
    } else if (isSafeLinkHref(match[3])) {
      nodes.push(
        <a
          key={`${keyPrefix}-a-${i}`}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
          style={{ color: "inherit" }}
        >
          {match[2]}
        </a>,
      );
    } else {
      nodes.push(match[2]);
    }
    lastIndex = pattern.lastIndex;
    i += 1;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderTextBlocks(text, keyPrefix) {
  const blocks = [];
  let current = null;
  const flush = () => {
    if (!current) return;
    const key = `${keyPrefix}-${blocks.length}`;
    const content = current.lines.join("\n").trim();
    if (current.step) {
      blocks.push(
        <section key={key} aria-label={`Schritt ${current.step}`} className="border-l-2 border-[#171014] py-0.5 pl-3">
          <p className="mb-1"><strong className="font-bold text-[#171014]">Schritt {current.step}</strong></p>
          {content && <p className="whitespace-pre-wrap">{renderInline(content, key)}</p>}
        </section>,
      );
    } else if (content) {
      blocks.push(<p key={key} className="whitespace-pre-wrap">{renderInline(content, key)}</p>);
    }
    current = null;
  };
  for (const line of text.split("\n")) {
    const heading = line.trim().replace(/^\*\*(Schritt\s+\d+[^*]*)\*\*$/i, "$1");
    const step = heading.match(/^\s*(?:\*\*)?Schritt\s+(\d+)(?:\*\*)?\s*[:.)]?\s*(?:\*\*(?=\s|$))?\s*(.*)$/i);
    if (step) {
      flush();
      current = { step: step[1], lines: [step[2]] };
    } else if (!line.trim()) {
      flush();
    } else {
      if (/^\s*(?:\*\*)?(?:Tipp|Beispiel|Klickweg|Kontrolle|Hinweis|Bild)\s*:/i.test(line)) flush();
      if (!current) current = { lines: [] };
      current.lines.push(line);
    }
  }
  flush();
  return blocks;
}

export function BotMessageContent({ text, onImageLoad }) {
  const imagePattern = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const blocks = [];
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = imagePattern.exec(text)) !== null) {
    blocks.push(...renderTextBlocks(text.slice(lastIndex, match.index), `txt-${i}`));
    if (isSafeImageSrc(match[2])) {
      blocks.push(<HelpScreenshot key={`img-${i}`} src={match[2]} alt={match[1]} onLoad={onImageLoad} />);
    }
    lastIndex = imagePattern.lastIndex;
    i += 1;
  }
  blocks.push(...renderTextBlocks(text.slice(lastIndex), "txt-last"));
  return <div className="space-y-3">{blocks}</div>;
}

export default function HelpChat() {
  const location = useLocation();
  const activeLocationContext = useOptionalActiveLocation();
  const activeLocation = activeLocationContext?.activeLocation || null;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const conversationRef = useRef(null);
  const sendingRef = useRef(false);
  const [messages, setMessages] = useState([GREETING]);
  const activeTheme = resolveDesignTheme(activeLocation || {});
  const themeAccent = activeTheme.accent || activeTheme.primary || "#1D1D1F";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || sendingRef.current) return;
    sendingRef.current = true;
    if (!conversationRef.current) conversationRef.current = newConversationId();

    const nextMessages = [...messages, { role: "user", text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      const res = await chatAPI.sendMessage({
        message: text,
        conversation_id: conversationRef.current,
        history: messages.slice(1).slice(-8).map((message) => ({
          role: message.role,
          text: message.text.slice(0, 2000),
        })),
        context: {
          area: helpArea(location.pathname),
          tab: document.querySelector('[role="tab"][data-state="active"], [role="tab"][aria-selected="true"]')?.textContent?.trim(),
        },
      });
      conversationRef.current = res.data.conversation_id || conversationRef.current;

      setMessages((prev) => [
        ...prev,
        { role: "bot", text: res.data.reply || HELP_CHAT_ERROR_TEXT },
      ]);
    } catch (error) {
      const expired = error?.response?.status === 410;
      if (expired) conversationRef.current = null;
      setMessages((prev) => [...prev, { role: "bot", text: expired
        ? "Diese Unterhaltung ist nach 30 Tagen abgelaufen. Starte bitte einen neuen Chat."
        : HELP_CHAT_ERROR_TEXT }]);
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-20 right-4 z-50 flex h-[min(600px,calc(100dvh-110px))] w-[min(420px,calc(100vw-32px))] flex-col rounded-2xl border border-[#E5E5EA] bg-white shadow-2xl">
          <div className="flex h-12 items-center justify-between border-b border-[#E5E5EA] px-4">
            <p className="text-sm font-semibold text-[#1D1D1F]">Hilfe-Chat</p>
            <div className="flex items-center gap-2">
              <button aria-label="Neuen Chat starten" disabled={loading} onClick={() => {
                conversationRef.current = null;
                setMessages([GREETING]);
                setInput("");
              }} className="rounded p-1 hover:bg-[#F5F5F7] disabled:opacity-40"><Plus className="h-4 w-4" /></button>
              <button aria-label="Hilfe-Chat schließen" onClick={() => setOpen(false)} className="rounded p-1 hover:bg-[#F5F5F7]">
              <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="border-b border-[#E5E5EA] px-4 py-2 text-xs text-[#6E6E73]">Der Verlauf wird für unser Support-Team 30 Tage gespeichert und danach automatisch gelöscht.</p>

          <div className="flex-1 space-y-2 overflow-auto bg-[#FAFAFB] p-3">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`w-fit max-w-[88%] px-3 py-2 text-sm leading-6 ${
                  message.role === "user"
                    ? "ml-auto whitespace-pre-wrap rounded-2xl rounded-br-none text-white"
                    : "mr-auto rounded-2xl rounded-bl-none border border-[#E5E5EA] bg-white text-[#1D1D1F]"
                }`}
                style={message.role === "user" ? { backgroundColor: themeAccent } : undefined}
              >
                {message.role === "user" ? (
                  message.text
                ) : (
                  <><BotMessageContent text={message.text} onImageLoad={index === messages.length - 1 ? () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }) : undefined} />
                  {/booking@makeit-media\.de/i.test(message.text) && <button type="button" className="mt-3 rounded-lg border px-3 py-2 font-medium" onClick={()=>setSupportOpen(true)}>E-Mail an Support senden</button>}</>
                )}
              </div>
            ))}

            {loading && (
              <div className="mr-auto flex items-center gap-1 px-2 py-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#86868B] [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#86868B] [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#86868B]" />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="flex gap-2 border-t border-[#E5E5EA] p-3">
            <input
              className="h-10 min-w-0 flex-1 rounded-xl border border-[#E5E5EA] px-3 text-sm"
              placeholder="Frage zur Anwendung..."
              aria-label="Frage an Mia"
              maxLength={4000}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") send();
              }}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Nachricht senden"
              className="grid h-10 w-10 place-items-center rounded-xl text-white disabled:opacity-60"
              style={{ backgroundColor: themeAccent }}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <HelpChatSupportDialog open={supportOpen} onOpenChange={setSupportOpen} messages={messages}/>
      <button
        onClick={() => setOpen((value) => !value)}
        className="fixed bottom-4 right-4 z-50 grid h-12 w-12 place-items-center rounded-full text-white shadow-lg hover:opacity-90"
        style={{ backgroundColor: themeAccent }}
        aria-label="Hilfe-Chat öffnen"
      >
        <MessageSquare className="h-5 w-5" />
      </button>
    </>
  );
}
